import "jstree/dist/themes/default/style.css";
import "assets/styles/jstree.custom.css";
import "jstree";

import $ from "jquery";
import React from "react";
import StringUtil from "utils/StringUtil";
import styled from "styled-components";

const TreeComponent = styled.div`
`

export interface UIJsTreeProps {
    rootNode?: { text?: string; type?: string; icon?: string };
    data: any[];
    dataKeys: {
        id: string;
        parent: string;
        level?: string;
        text?: string;
        type?: string;
        icon?: string;
    };
    initiallyOpenLevel?: number;
    defaultSelectId?: string;
    multiple?: boolean;
    plugins?: string[];
    types?: { [key: string]: { [key: string]: string } };
    jsTreeCreateStates?: any[];
    onSelectNode?: (event, data: { node: any; selected: string[] }) => void;
}

export default UIJsTree;

function UIJsTree(props: UIJsTreeProps) {
    const [jstreeLoaded, setJstreeLoaded] = React.useState<boolean>(false);
    const [openedMap] = React.useState<{ [id: string]: boolean }>({});
    const types = props.types || {
        folder : {"icon": "jstree-folder"},
        file: {"icon": "jstree-file"}
    };
    const plugins = props.plugins || ["types"];

    let $jsTreeElem;

    React.useEffect(() => {
        return () => {
            $jsTreeElem.jstree("destroy");
        };
    }, [$jsTreeElem]);

    React.useEffect(() => {
        setJstreeLoaded(false);

        let selectedIds: string[] = $jsTreeElem.jstree("get_selected");

        if (selectedIds instanceof $) {
            selectedIds = [];
        }

        $jsTreeElem
            .jstree("destroy")
            .jstree({
                core: {
                    multiple: props.multiple === true,
                    data: getTreeData(selectedIds),
                },
                plugins: plugins,
                types: types,
            })
            .on("loaded.jstree", (event) => {
                setJstreeLoaded(true);
            })
            .on("after_open.jstree", (event, data) => {
                openedMap[data.node.id] = true;
            })
            .on("after_close.jstree", (event, data) => {
                openedMap[data.node.id] = false;
            })
            .on("changed.jstree", (event, data) => {
                if (props.onSelectNode != null && data.node != null) {
                    props.onSelectNode(event, data);
                }
            });
    }, [$jsTreeElem, props.data, ...(props.jsTreeCreateStates || [])]);

    React.useEffect(
        () => {
            $jsTreeElem.jstree("deselect_all", true);
            $jsTreeElem.jstree("select_node", [StringUtil.defaultIfEmpty(props.defaultSelectId, "0")], true);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.defaultSelectId, jstreeLoaded]
    );

    const getTreeData = (selectedIds: string[]): any[] => {
        const selectedMap: { [id: string]: boolean } = {};
        const treeData: any[] = [];

        if (selectedIds != null && typeof selectedIds.forEach === "function") {
            selectedIds.forEach((id) => (selectedMap[id] = true));
        }

        if (props.rootNode != null) {
            treeData.push({
                id: "0",
                parent: "#",
                text: props.rootNode.text,
                icon: props.rootNode.icon,
                type: props.rootNode.type,
                state: {
                    opened: true,
                    selected: selectedMap["0"] === true || (StringUtil.defaultIfEmpty(props.defaultSelectId, "0") === "0" && selectedIds.length === 0),
                },
                data: props.rootNode,
            });
        }

        if (props.data == null) {
            return [];
        }

        props.data.forEach((data) => {
            const treeNode: any = {};

            treeNode.id = data[props.dataKeys.id];
            treeNode.parent = data[props.dataKeys.parent];

            if (props.dataKeys.text != null) {
                treeNode.text = data[props.dataKeys.text];
            }

            if (props.dataKeys.type != null) {
                treeNode.type = data[props.dataKeys.type].toLowerCase();
            }

            if (props.dataKeys.icon != null) {
                treeNode.icon = data[props.dataKeys.icon];
            }

            treeNode.state = {
                opened: openedMap[treeNode.id] === true,
                selected: selectedMap[treeNode.id] === true || (props.defaultSelectId === treeNode.id && selectedIds.length === 0),
                disabled: false,
            };

            if (props.initiallyOpenLevel === -1) {
                treeNode.state.opened = openedMap[treeNode.id] !== false;
            } else if (props.initiallyOpenLevel != null && props.dataKeys.level != null && getOpenedMapSize() === 0) {
                treeNode.state.opened = data[props.dataKeys.level] <= props.initiallyOpenLevel;
            }

            if (treeNode.parent == null || treeNode.parent == '') {
                treeNode.parent = props.rootNode == null ? "#" : "0";
            }

            treeNode.data = data;

            treeData.push(treeNode);
        });

        return treeData;
    };

    const getOpenedMapSize = (): number => {
        let size = 0;

        for (let key in openedMap) {
            if (openedMap[key] != null) {
                size++;
            }
        }

        return size;
    };

    return <TreeComponent ref={(element) => ($jsTreeElem = $(element!))} className="jstree-container"/>;
}
